import cn from 'classnames';
import { FC, useMemo } from 'react';

import { PatientTranscriptorEncounterStatus } from '@/shared/api/protocol-ts/model/dto_patient_pb';
import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { useAppSelector, useIsNonDentalOrganization } from '@/shared/hooks';

import { patientModel } from '@/entities/patient';
import { CountsIconNames } from '@/entities/patient';
import { StudyCounter } from '@/entities/patient/ui/StudyCounter/StudyCounter';
import { studyCountModel } from '@/entities/studyCount';

import styles from './StudiesCounters.module.scss';

type StudiesCountersVariants = 'row' | 'card';

type StudiesCountersProps = {
  patientID: string;
  variant?: StudiesCountersVariants;
  className?: string;
  iconSize?: number;
};

export const StudiesCounters: FC<StudiesCountersProps> = (props) => {
  const { patientID, className, variant = 'row', iconSize } = props;
  const isNonDental = useIsNonDentalOrganization();

  const patientStudies = useAppSelector(
    studyCountModel.selectors.selectStudyCountsByPatientID(patientID),
  );

  const patient = useAppSelector((state) =>
    patientModel.selectors.selectById(state, patientID),
  );

  const transcriptorCount = useMemo(() => {
    if (!patient?.TranscriptorEncounters) {
      return 0;
    }

    return patient.TranscriptorEncounters.filter(
      (encounter) =>
        encounter.Status ===
        PatientTranscriptorEncounterStatus.PatientTranscriptorEncounterStatus_Active,
    ).length;
  }, [patient?.TranscriptorEncounters]);

  const studies = useMemo(() => {
    // TODO: [8h] Move this computation into reduce in patient list stream to avoid recalculating in every component instance
    const defaultStudies: Record<
      Exclude<StudyType, StudyType.StudyType_InvalidValue>,
      { iconName: CountsIconNames; count: number }
    > = {
      [StudyType.StudyType_CBCT]: {
        iconName: 'cbctNew',
        count: 0,
      },

      [StudyType.StudyType_IntraoralXRay]: {
        iconName: 'ioxray',
        count: 0,
      },

      [StudyType.StudyType_PanoramicXRay]: {
        iconName: 'pano',
        count: 0,
      },

      [StudyType.StudyType_IOS_Meshes]: {
        iconName: 'ios',
        count: 0,
      },
      [StudyType.StudyType_DentalPhoto]: {
        iconName: 'photo',
        count: 0,
      },
    };

    if (!patientStudies) return defaultStudies;

    patientStudies?.counts?.forEach(({ Count, Modality }) => {
      // Type assertion is safe here because we check if the key exists in defaultStudies
      // and Modality comes from backend with the same StudyType enum values
      if (defaultStudies[Modality as keyof typeof defaultStudies]) {
        defaultStudies[Modality as keyof typeof defaultStudies].count = Count;
      }
    });

    return defaultStudies;
  }, [patientStudies]);

  return (
    <div
      data-component="studies-count-counters"
      className={cn(
        styles.container,
        variant === 'card' && styles.cardContainer,
        className,
      )}
    >
      {!isNonDental &&
        Object.values(studies).map(({ iconName, count }) => (
          <StudyCounter
            name={iconName}
            count={count}
            key={iconName}
            variant={variant}
            iconSize={iconSize}
          />
        ))}
      <StudyCounter
        name="scribe"
        count={transcriptorCount}
        key="scribe"
        variant={variant}
        iconSize={iconSize}
      />
    </div>
  );
};
